import { Box } from '@mui/material';
import { CountrySelect, CountrySelectCountry } from './CountrySelect';
import { StyledTextField, TextInputProps } from './TextInput';
import React from 'react';
import { InputLabel } from './InputLabel';
import { omit } from 'lodash';
import { useTheme } from '@emotion/react';

export interface PhoneNumberInputProps
  extends Omit<TextInputProps, 'type' | 'value' | 'defaultValue' | 'onChange'> {
  value?: CountrySelectCountry['value'] & { phoneNumber?: string };
  onChange?: (value: PhoneNumberInputProps['value']) => void;
}

/**
 * PhoneNumberInput component, part of Delogue design system.
 * @param variant - visual variant of the input, either 'filled', 'outlined', 'standard', or 'unstyled', default is 'outlined'
 * @param label - label text to display above the input
 * @param required - whether the input is required, will show a * after the label
 * @param onChange - callback function to be called when the phone number or country changes
 * @param value - controlled value containing country information and phone number
 * @param disabled - whether the input is disabled
 * @param error - whether the input is in an error state
 * @param helperText - helper text to display below the input, will be displayed in red if the input is in an error state
 * @param placeholder - placeholder text to display when the input is empty
 * @param onBlur - callback function to be called when the input loses focus
 * @param onFocus - callback function to be called when the input receives focus
 * @param onKeyDown - callback function to be called when a key is pressed
 * @param onPaste - callback function to be called when content is pasted
 * @param labelSeparate - whether to display the label separately above the input
 * @param labelVariant - typography variant for the label
 * @param labelWeight - font weight for the label
 * @param labelColor - color of the label text
 * @param isUnderline - whether to display an underline style for the label
 */
const BasePhoneNumberInput = ({
  variant = 'outlined',
  label,
  required,
  onChange,
  ...rest
}: PhoneNumberInputProps) => {
  const theme = useTheme();

  const [phoneNumber, setPhoneNumber] = React.useState('');
  const onNumberChange: TextInputProps['onChange'] = e => {
    e.stopPropagation();
    e.preventDefault();
    if (/^\d*$/.test(e.target.value)) {
      if (onChange) {
        onChange({ ...rest?.value, phoneNumber: e.target.value } as PhoneNumberInputProps['value']);
      } else {
        setPhoneNumber(e.target.value);
      }
    }
  };
  const onCountryChange: CountrySelectCountry['onChange'] = (inputValue, e, c) => {
    e.stopPropagation();
    e.preventDefault();
    onChange?.({ ...rest?.value, ...(inputValue ?? {}) } as PhoneNumberInputProps['value']);
  };
  return (
    <Box width="100%">
      {variant === 'outlined' && rest.labelSeparate && (
        <InputLabel
          isUnderline={rest?.isUnderline}
          labelVariant={rest?.labelVariant ?? 'labelSm'}
          labelColor={rest?.labelColor}
          labelWeight={rest?.labelWeight ?? 'regular'}
          required={required}
          error={rest?.error}
          disabled={rest.disabled}
        >
          {label}
        </InputLabel>
      )}
      <Box display="flex" gap="1rem" flexDirection="row" alignItems="flex-start">
        <CountrySelect
          valueType="countryObj"
          variant={variant === 'unstyled' ? 'standard' : variant}
          labelSeparate={rest.labelSeparate}
          showCountryCode
          required={required}
          disabled={rest?.disabled}
          id={`phoneNumber-input-country-select-${rest?.name}`}
          sx={{
            maxWidth: '118px',
            color: theme.palette.text.quaternary,
            '& .MuiInputBase-root ': {
              height: '44px',
              width: '100%',
            },
            '& .MuiSelect-select span:nth-of-type(3)': {
              display: 'none',
            },
          }}
          value={rest?.value}
          onChange={onCountryChange}
        />
        <StyledTextField
          fullWidth
          required={required}
          {...(!rest.labelSeparate ? { label } : {})}
          {...omit(rest, ['name'])}
          variant={variant === 'unstyled' ? 'standard' : variant}
          onBlur={e => {
            rest?.onBlur?.(e);
          }}
          value={rest?.value?.phoneNumber ?? phoneNumber}
          onChange={onNumberChange}
        />
      </Box>
    </Box>
  );
};

export const PhoneNumberInput = BasePhoneNumberInput;
